import { useMemo, useRef, useState } from "react";
import { applyCoding, makeNettoData, parseNettoData, parsemk60_m3, parsemk60_nonm } from "./Coding";

type Props = Readonly<{
    isM3: boolean,
    codingData:  Uint8Array
    setCodingData: (data: Uint8Array) => void
}>;

export const CodingEditor = ({isM3, codingData, setCodingData}: Props): JSX.Element => {
    const parsedCoding = useMemo(() => {
        return isM3 ? parsemk60_m3(codingData) : parsemk60_nonm(codingData);
    }, [isM3, codingData]);

    const fileInput = useRef<HTMLInputElement>(null);
    const [fileError, setFileError] = useState<string | undefined>();

    const onSaveTrc = () => {
        const blob = new Blob([makeNettoData(codingData)], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'coding.trc';
        a.click();
        URL.revokeObjectURL(url);
    };

    const onLoadTrc = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Clear the input so picking the same file again still fires onChange
        e.target.value = '';
        if (!file) {
            return;
        }

        try {
            const loaded = parseNettoData(await file.text());
            // Loaded data replaces the block read from the module, so it has to be the same size
            if (loaded.length !== codingData.length) {
                throw new Error(`File contains ${loaded.length} bytes of coding data, module has ${codingData.length}`);
            }
            setFileError(undefined);
            setCodingData(loaded);
        } catch (err) {
            setFileError(`Failed to load ${file.name}: ${err instanceof Error ? err.message : err}`);
        }
    };

    const rows = Object.keys(parsedCoding.parsed).map(name => {
        const field = parsedCoding.fields.find(f => f.name === name);
        const value = parsedCoding.parsed[name];
        const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
                parsedCoding.parsed[name] = e.target.value;
                setCodingData(applyCoding(codingData, parsedCoding.parsed, parsedCoding.fields));
            };

        // If we have defined values for this field, show a dropdown of those options
        // If not, show the raw value
        const valueView =
            field?.values && typeof value === 'string' && Object.keys(field.values).includes(value)
            ? (<select onChange={onChange}>
                {Object.keys(field?.values!).map(k => (
                    <option key={k} value={k} selected={k === value}>
                        {k}
                    </option>
                    ))}
            </select>)
            : value;

        return (<>
                <tr key={name} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2 font-medium">{name}</td>
                <td className="px-4 py-2">{valueView}</td>
                <td className="px-4 py-2">
                    { field?.recommend !== undefined && value !== field.recommend && field.recommend }
                </td>
                </tr>
            </>);
    });

    return (
        <>
        <div>
            <button type='button' onClick={onSaveTrc}>Save .trc</button>
            <button type='button' onClick={() => fileInput.current?.click()}>Load .trc</button>
            <input ref={fileInput} type='file' accept='.trc,.txt' style={{ display: 'none' }} onChange={onLoadTrc} />
            {fileError && <p>{fileError}</p>}
        </div>
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="min-w-full text-sm text-left text-gray-800">
            <thead className="bg-gray-100 text-gray-600 uppercase text-xs font-semibold">
            <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Current Value</th>
                <th className="px-4 py-2">Recommended</th>
            </tr>
            </thead>
            <tbody>{rows}</tbody>
        </table>
        </div>
        </>
    );
};
